// frontend/src/pages/ParametrizacionContablePage.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { FiEdit, FiTrash2, FiPlus, FiSave, FiX, FiInfo } from 'react-icons/fi';
import Loading from '../components/Loading';
import './ParametrizacionContablePage.css';

const CAMPO_LABELS = {
  cuenta_iva_credito: { label: 'IVA Crédito Fiscal', tooltip: 'Cuenta para registrar el IVA crédito fiscal en compras' },
  cuenta_iva_debito: { label: 'IVA Débito Fiscal', tooltip: 'Cuenta para registrar el IVA débito fiscal en ventas' },
  cuenta_por_pagar: { label: 'Cuenta por Pagar', tooltip: 'Cuenta de pasivo para registrar obligaciones de pago' },
  cuenta_por_cobrar: { label: 'Cuenta por Cobrar', tooltip: 'Cuenta de activo para registrar derechos de cobro' },
  cuenta_gasto_defecto: { label: 'Gasto por Defecto', tooltip: 'Cuenta de gasto que se asigna por defecto al crear asientos' },
  cuenta_ingreso_defecto: { label: 'Ingreso por Defecto', tooltip: 'Cuenta de ingreso que se asigna por defecto al crear asientos' },
};

const TIPO_COLORES = {
  factura_compra: '#3b82f6',
  factura_venta: '#10b981',
  boleta: '#8b5cf6',
  nota_credito: '#f59e0b',
  nota_debito: '#ef4444',
  guia_despacho: '#6b7280',
  factura_exenta: '#06b6d4',
  boleta_honorarios: '#ec4899',
};

const ParametrizacionContablePage = () => {
  const [parametrizaciones, setParametrizaciones] = useState([]);
  const [planCuentas, setPlanCuentas] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    cargarDatos();
  }, []);

  const getApiBase = () => {
    const baseURL = api?.defaults?.baseURL || '/api/content';
    return baseURL.replace(/\/content\/?$/i, '/contabilidad');
  };

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = getApiBase();

      const [respParametrizaciones, respPlanCuentas, respTipos] = await Promise.all([
        api.get(`${API_BASE_URL}/parametrizaciones-asientos/`),
        api.get(`${API_BASE_URL}/plan-cuentas/`),
        api.get(`${API_BASE_URL}/parametrizaciones-asientos/tipos_documento/`),
      ]);

      setParametrizaciones(respParametrizaciones.data.results || respParametrizaciones.data);
      setPlanCuentas(respPlanCuentas.data.results || respPlanCuentas.data);
      setTiposDocumento(respTipos.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar las parametrizaciones');
    } finally {
      setLoading(false);
    }
  };

  const getCamposPorTipo = (tipoDocumento) => {
    const tipo = tiposDocumento.find(t => t.value === tipoDocumento);
    return tipo?.campos || {};
  };

  const handleEdit = (parametrizacion) => {
    setEditingId(parametrizacion.id);
    setValidationErrors({});
    setFormData({
      tipo_documento: parametrizacion.tipo_documento,
      cuenta_iva_credito: parametrizacion.cuenta_iva_credito || '',
      cuenta_iva_debito: parametrizacion.cuenta_iva_debito || '',
      cuenta_por_pagar: parametrizacion.cuenta_por_pagar || '',
      cuenta_por_cobrar: parametrizacion.cuenta_por_cobrar || '',
      cuenta_gasto_defecto: parametrizacion.cuenta_gasto_defecto || '',
      cuenta_ingreso_defecto: parametrizacion.cuenta_ingreso_defecto || '',
      activo: parametrizacion.activo
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({});
    setValidationErrors({});
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue;

    if (type === 'checkbox') {
      finalValue = checked;
    } else if (name === 'tipo_documento') {
      finalValue = value;
    } else {
      finalValue = value === '' ? null : parseInt(value);
    }

    setFormData(prev => ({ ...prev, [name]: finalValue }));
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validateForm = () => {
    const campos = getCamposPorTipo(formData.tipo_documento);
    const errors = {};

    if (!formData.tipo_documento) {
      errors.tipo_documento = true;
    }

    Object.entries(campos).forEach(([campo, meta]) => {
      if (meta.required && !formData[campo]) {
        errors[campo] = true;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (parametrizacionId) => {
    if (!validateForm()) {
      toast.error('Complete los campos requeridos marcados con *');
      return;
    }

    try {
      const API_BASE_URL = getApiBase();
      const campos = getCamposPorTipo(formData.tipo_documento);
      const dataToSend = {
        tipo_documento: formData.tipo_documento,
        activo: formData.activo,
      };
      Object.keys(campos).forEach(campo => {
        dataToSend[campo] = formData[campo] || null;
      });

      if (parametrizacionId) {
        await api.put(`${API_BASE_URL}/parametrizaciones-asientos/${parametrizacionId}/`, dataToSend);
        toast.success('Parametrización actualizada exitosamente');
      } else {
        await api.post(`${API_BASE_URL}/parametrizaciones-asientos/`, dataToSend);
        toast.success('Parametrización creada exitosamente');
      }

      setEditingId(null);
      setFormData({});
      setValidationErrors({});
      cargarDatos();
    } catch (error) {
      console.error('Error al guardar parametrización:', error);
      const errorMsg = error.response?.data?.tipo_documento?.[0] ||
                      error.response?.data?.detail ||
                      'Error al guardar la parametrización';
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (parametrizacionId) => {
    if (!window.confirm('¿Está seguro de eliminar esta parametrización?')) {
      return;
    }

    try {
      const API_BASE_URL = getApiBase();
      await api.delete(`${API_BASE_URL}/parametrizaciones-asientos/${parametrizacionId}/`);
      toast.success('Parametrización eliminada exitosamente');
      cargarDatos();
    } catch (error) {
      console.error('Error al eliminar parametrización:', error);
      toast.error('Error al eliminar la parametrización');
    }
  };

  const handleCrearNueva = () => {
    setEditingId('new');
    setValidationErrors({});
    setFormData({
      tipo_documento: '',
      cuenta_iva_credito: '',
      cuenta_iva_debito: '',
      cuenta_por_pagar: '',
      cuenta_por_cobrar: '',
      cuenta_gasto_defecto: '',
      cuenta_ingreso_defecto: '',
      activo: true
    });
  };

  const getCuentaDisplay = (cuentaId, codigo, nombre) => {
    if (!cuentaId) return '-';
    return `${codigo} - ${nombre}`;
  };

  const getCuentasImputables = () => {
    return planCuentas.filter(cuenta => cuenta.imputa);
  };

  const getTipoLabel = (tipoValue) => {
    const tipo = tiposDocumento.find(t => t.value === tipoValue);
    return tipo?.label || tipoValue;
  };

  const getTipoColor = (tipoValue) => {
    return TIPO_COLORES[tipoValue] || '#6b7280';
  };

  const renderCampoSelect = (campo, meta, isEditing) => {
    const info = CAMPO_LABELS[campo];
    const isRequired = meta.required;
    const hasError = validationErrors[campo];

    if (!isEditing) {
      return null;
    }

    return (
      <div className="param-field" key={campo}>
        <label className="param-field-label">
          {info.label}
          {isRequired && <span className="required-asterisk">*</span>}
          <span className="field-tooltip" title={info.tooltip}>
            <FiInfo size={12} />
          </span>
        </label>
        <select
          name={campo}
          value={formData[campo] || ''}
          onChange={handleChange}
          className={`select-input${hasError ? ' select-error' : ''}`}
        >
          <option value="">Sin cuenta...</option>
          {getCuentasImputables().map(cuenta => (
            <option key={cuenta.id} value={cuenta.id}>
              {cuenta.codigo} - {cuenta.nombre}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderCampoReadonly = (campo, param) => {
    const info = CAMPO_LABELS[campo];
    const cuentaId = param[campo];
    const codigo = param[`${campo}_codigo`];
    const nombre = param[`${campo}_nombre`];
    const display = getCuentaDisplay(cuentaId, codigo, nombre);

    return (
      <div className="param-field" key={campo}>
        <label className="param-field-label">{info.label}</label>
        <span className="param-field-value">{display}</span>
      </div>
    );
  };

  const renderCard = (param) => {
    const isEditing = editingId === param.id;
    const campos = getCamposPorTipo(param.tipo_documento);
    const color = getTipoColor(param.tipo_documento);

    return (
      <div className={`param-card${isEditing ? ' param-card-editing' : ''}`} key={param.id}>
        <div className="param-card-header" style={{ borderLeftColor: color }}>
          <div className="param-card-header-left">
            <span className="tipo-documento-badge" style={{ backgroundColor: `${color}15`, color }}>
              {param.tipo_documento_display}
            </span>
            <span className={`badge ${param.activo ? 'badge-activo' : 'badge-inactivo'}`}>
              {param.activo ? 'Activo' : 'Inactivo'}
            </span>
          </div>
          <div className="param-card-actions">
            {isEditing ? (
              <>
                <button className="btn-icon btn-success" onClick={() => handleSave(param.id)} title="Guardar">
                  <FiSave />
                </button>
                <button className="btn-icon btn-danger" onClick={handleCancel} title="Cancelar">
                  <FiX />
                </button>
              </>
            ) : (
              <>
                <button className="btn-icon" onClick={() => handleEdit(param)} title="Editar">
                  <FiEdit />
                </button>
                <button className="btn-icon btn-danger" onClick={() => handleDelete(param.id)} title="Eliminar">
                  <FiTrash2 />
                </button>
              </>
            )}
          </div>
        </div>
        <div className="param-card-body">
          {isEditing && (
            <div className="param-field">
              <label className="param-field-label">
                Estado
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="activo"
                  checked={formData.activo}
                  onChange={handleChange}
                />
                Activo
              </label>
            </div>
          )}
          <div className="param-fields-grid">
            {Object.entries(campos).map(([campo, meta]) =>
              isEditing
                ? renderCampoSelect(campo, meta, true)
                : renderCampoReadonly(campo, param)
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderNewCard = () => {
    if (editingId !== 'new') return null;

    const campos = getCamposPorTipo(formData.tipo_documento);
    const color = getTipoColor(formData.tipo_documento);

    return (
      <div className="param-card param-card-editing param-card-new">
        <div className="param-card-header" style={{ borderLeftColor: formData.tipo_documento ? color : '#d1d5db' }}>
          <div className="param-card-header-left">
            <span className="param-card-new-title">Nueva Parametrización</span>
          </div>
          <div className="param-card-actions">
            <button className="btn-icon btn-success" onClick={() => handleSave(null)} title="Guardar">
              <FiSave />
            </button>
            <button className="btn-icon btn-danger" onClick={handleCancel} title="Cancelar">
              <FiX />
            </button>
          </div>
        </div>
        <div className="param-card-body">
          <div className="param-field param-field-full">
            <label className="param-field-label">
              Tipo de Documento
              <span className="required-asterisk">*</span>
            </label>
            <select
              name="tipo_documento"
              value={formData.tipo_documento}
              onChange={handleChange}
              className={`select-input${validationErrors.tipo_documento ? ' select-error' : ''}`}
            >
              <option value="">Seleccionar tipo...</option>
              {tiposDocumento.map(tipo => (
                <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
              ))}
            </select>
          </div>

          {formData.tipo_documento && (
            <>
              <div className="param-field">
                <label className="param-field-label">Estado</label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="activo"
                    checked={formData.activo}
                    onChange={handleChange}
                  />
                  Activo
                </label>
              </div>
              <div className="param-fields-grid">
                {Object.entries(campos).map(([campo, meta]) =>
                  renderCampoSelect(campo, meta, true)
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="parametrizacion-page">
      <div className="page-header">
        <h1>Parametrización Contable</h1>
        <button className="btn-primary" onClick={handleCrearNueva} disabled={editingId === 'new'}>
          <FiPlus /> Nueva Parametrización
        </button>
      </div>

      <div className="info-message">
        <p>
          Configure las cuentas contables a utilizar para cada tipo de documento.
          Estas cuentas se usarán automáticamente al crear asientos contables desde documentos legales.
          Solo se muestran los campos relevantes para cada tipo de documento.
        </p>
      </div>

      {renderNewCard()}

      <div className="param-cards-grid">
        {parametrizaciones.length === 0 && editingId !== 'new' ? (
          <div className="empty-state">
            <p>No hay parametrizaciones configuradas. Cree una nueva para comenzar.</p>
          </div>
        ) : (
          parametrizaciones.map(param => renderCard(param))
        )}
      </div>
    </div>
  );
};

export default ParametrizacionContablePage;
